<template>
  <div class="settings-page">
    <div class="title-name-row">
      <h2>Organization</h2>
    </div>
    <span class="org-id">
      {{ orgId }}
      <button
        class="copy-btn"
        v-tippy="{ content: 'Copy id', arrow: false, placement: 'top' }"
        @click="copyOrgId"
      >
        <CopyIcon />
      </button>
    </span>

    <section v-if="organization" class="org-details">
      <div class="form-section">
        <div class="form-group">
          <label>Organization Name</label>
          <div class="input-container">
            <FormInput
              ref="nameInputRef"
              v-model:value="newOrgName"
              type="text"
              placeholder="Organization name"
              :state="
                errorInName
                  ? 'error'
                  : canRenameOrganization
                    ? 'default'
                    : 'disabled'
              "
              @keydown.enter="saveOrganizationName"
            />
            <BaseButton
              kind="main"
              type="primary"
              text="Save"
              :state="canRenameOrganization ? 'default' : 'disabled'"
              :tooltip="
                canRenameOrganization
                  ? undefined
                  : 'You are not allowed to perform this action.'
              "
              :tooltip-display="canRenameOrganization ? 'none' : 'always'"
              @click="saveOrganizationName"
            />
          </div>
        </div>
        <BaseAlert v-if="errorInName" state="error">
          Organization name cannot be empty.
        </BaseAlert>
      </div>
      <BaseAlert v-if="organization?.personal" state="info">
        <p>
          This is your default organization and cannot be deleted. It was
          automatically created with your account.
        </p>
      </BaseAlert>
    </section>

    <div v-if="!organization?.personal" class="advanced-section">
      <div
        class="advanced-header"
        @click="isAdvancedCollapsed = !isAdvancedCollapsed"
      >
        <BaseButton kind="auxiliary" type="secondary" class="collapse-button">
          <template #title> Advanced </template>
          <template #icon>
            <ChevronRightIcon :class="{ expanded: !isAdvancedCollapsed }" />
          </template>
        </BaseButton>
      </div>

      <template v-if="!isAdvancedCollapsed">
        <BaseAlert state="error" layout="vertical">
          <p>These actions are irreversible. Please be careful.</p>
          <BaseButton
            kind="auxiliary"
            type="primary"
            tint="red"
            text="Delete Organization"
            :state="canDeleteOrganization ? 'default' : 'disabled'"
            :tooltip="
              canDeleteOrganization
                ? undefined
                : 'You are not allowed to perform this action.'
            "
            :tooltip-display="canDeleteOrganization ? 'none' : 'always'"
            @click="deleteOrganization"
          >
            <template #icon>
              <DeleteIcon />
            </template>
          </BaseButton>
        </BaseAlert>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs, watch } from "vue";

import { copy } from "@/clipboard";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import * as inject from "@/components/inject";
import { openDeleteOrganizationModal } from "@/components/organizations";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

export default defineComponent({
  name: "Info",
  components: {
    BaseAlert,
    BaseButton,
    ChevronRightIcon,
    CopyIcon,
    DeleteIcon,
    FormInput,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { orgId } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();

    const nameInputRef = ref<HTMLInputElement | null>(null);
    const errorInName = ref(false);
    const isAdvancedCollapsed = ref(true);
    const releaser = useReleaser();
    const organization = await releaser.add(
      bindAsync(orgId, async (id) =>
        id ? controller.organization.get(id) : releasableRef(null)
      )
    );
    const newOrgName = ref(organization.value?.name ?? "");

    // Update newOrgName when organization changes (e.g., when navigating between orgs)
    releaser.defer(
      watch(
        () => organization.value?.name,
        (name) => {
          newOrgName.value = name ?? "";
        }
      )
    );

    const saveOrganizationName = async () => {
      if (newOrgName.value.trim()) {
        errorInName.value = false;
        await controller.organization.rename(orgId.value, newOrgName.value);
      } else {
        errorInName.value = true;
      }
    };

    const canDeleteOrganization = computed(() => {
      return organization.value?.permissions?.includes("delete") ?? false;
    });

    const canRenameOrganization = computed(() => {
      return organization.value?.permissions?.includes("rename") ?? false;
    });

    const copyOrgId = async () => {
      await copy(orgId.value);
      controller.notifications.notify("Organization ID copied to clipboard");
    };

    const deleteOrganization = async () => {
      // Prevent deletion of default/personal organization
      if (organization.value?.personal) {
        controller.notifications.notify(
          "Cannot delete the default organization. This organization was automatically created with your account."
        );
        return;
      }

      const orgName = organization.value?.name ?? "";

      openDeleteOrganizationModal(orgId.value, orgName, controller, router);
    };

    return {
      copyOrgId,
      organization,
      newOrgName,
      errorInName,
      nameInputRef,
      isAdvancedCollapsed,
      canDeleteOrganization,
      canRenameOrganization,
      saveOrganizationName,
      deleteOrganization,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss" as *;

.title-name-row {
  display: flex;
  align-items: center;
  gap: 6px;

  h2 {
    margin: 0;
    margin-bottom: 4px;
  }
}

.org-id {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  @extend %font-small-regular;

  @include themed() {
    color: t($grey-40);
  }

  .copy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    opacity: 0.7;
    @include transition(opacity 0.2s ease-out);

    &:hover {
      opacity: 1;
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }
}

.org-details {
  margin-top: 32px;

  .form-section {
    margin-bottom: 8px;

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0;
      margin-bottom: 8px;

      label {
        @extend %font-body-medium;
        margin-bottom: 8px;
        display: block;
        margin-inline-end: 24px;

        @include themed() {
          color: t($primary);
        }
      }

      .input-container {
        display: flex;
        flex-direction: row;
        gap: 8px;

        .base-button {
          flex-shrink: 0;
        }
      }
    }
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    @extend %font-body-medium;

    @include themed() {
      color: t($primary);
    }
  }

  .value {
    @extend %font-body-regular;
    padding: 8px 12px;
    border-radius: 6px;

    @include themed() {
      background-color: t($grey-05);
      color: t($grey-60);
      border: 1px solid t($grey-10);
    }
  }
}

.advanced-section {
  .advanced-header {
    display: flex;
    align-items: center;
    gap: 4px;
    user-select: none;
    padding-bottom: 12px;

    h3 {
      margin: 0;
      @extend %font-h4;

      @include themed() {
        color: t($primary);
      }
    }
  }

  .base-alert {
    margin-top: 16px;
  }
}

:deep(.collapse-button) {
  padding: 4px;

  .title {
    @extend %font-h4;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-inline-end: 4px;
  }

  .icon {
    width: 12px;
    height: 12px;

    svg {
      width: 12px;
      height: 12px;
      transition: transform 0.2s ease;

      &.expanded {
        transform: rotate(90deg);
      }
    }
  }
}
</style>
